// The floor plan display, decided.
//
// A tablet at the entrance shows one office's plan and who is sitting where today.
// It has no viewer: nothing on it is "yours", nothing on it can be booked, and the
// only question it answers is "who is at that desk". The rules below turn the same
// API answers the Bookings page uses (desks, today's bookings, the desks released
// by absent owners) into the state FloorPlanMap already knows how to draw, so the
// plan on the tablet is the plan people already recognise from the app.
//
// Kept out of the component so the rules can be run without a browser.

// AM before PM before full, so a desk booked twice for half days names the morning person.
const SLOT_ORDER = { am: 0, pm: 1, full: 2 };
const bySlot = (a, b) => (SLOT_ORDER[a.slot] ?? 9) - (SLOT_ORDER[b.slot] ?? 9);

// Whether a reserved desk is open for the whole of today, by the server's answer.
const releasedToday = (desk, released, date) => {
  const slots = released?.[desk.id] || {};
  return slots[date] === 'full';
};

/**
 * What one tile says today. Returns the shape FloorPlanMap's getState expects:
 *   { status: 'taken', person, person_color, slot }
 *   { status: 'reserved' } — drawn with the owner's initials, which is the point
 *   { status: 'blocked' }
 *   { status: 'available', released }
 * Never 'mine': a shared screen has nobody to be mine for.
 */
export function displayDeskState(desk, { bookings = [], released = {}, date }) {
  if (desk.status === 'blocked') return { status: 'blocked' };

  const on = bookings
    .filter(b => b.resource_id === desk.id && b.booking_date === date)
    .sort(bySlot);
  if (on.length) {
    const first = on[0];
    // Two bookings on one desk can only be an AM and a PM (the server refuses a clash),
    // which together are the whole day.
    const wholeDay = on.length > 1 || first.slot === 'full';
    return {
      status: 'taken',
      person: first.guest_name || first.person_name,
      person_color: first.avatar_color,
      person_title: first.guest_name ? `Guest of ${first.person_name}` : null,
      slot: wholeDay ? 'full' : first.slot,
    };
  }

  const open = releasedToday(desk, released, date);
  if (desk.status === 'reserved' && !open) return { status: 'reserved', note: desk.status_note };
  if (!desk.is_active && !open && desk.status !== 'bookable') return { status: 'blocked' };
  return { status: 'available', released: desk.status === 'reserved' && open };
}

/**
 * Everybody at one of these desks today, for the list beside the plan.
 * [{ person, host, desk_id, desk_label, slot, color }] sorted by surname.
 */
export function whoIsIn(desks, bookings, date) {
  const byId = new Map(desks.map(d => [d.id, d]));
  const surname = (name) => String(name || '').trim().split(/\s+/).pop() || '';
  return bookings
    .filter(b => b.booking_date === date && byId.has(b.resource_id))
    .map(b => ({
      person: b.guest_name || b.person_name,
      host: b.guest_name ? b.person_name : null,
      desk_id: b.resource_id,
      desk_label: byId.get(b.resource_id).label,
      slot: b.slot,
      color: b.avatar_color,
    }))
    .sort((a, b) => surname(a.person).localeCompare(surname(b.person)) || a.person.localeCompare(b.person));
}

/**
 * How many people are in and how many seats a visitor could still take.
 * People, not occupied desks: a morning and an afternoon on one desk are two people,
 * and the list beside the plan names both of them.
 */
export function boardCounts(desks, bookings, released, date) {
  const states = desks.map(d => displayDeskState(d, { bookings, released, date }).status);
  return {
    in: whoIsIn(desks, bookings, date).length,
    free: states.filter(s => s === 'available').length,
    bookable: states.filter(s => s === 'taken' || s === 'available').length,
  };
}

/**
 * Which office to show: the one asked for if it has desks, otherwise the first
 * that does. An office with no desks has no plan, so it is never chosen.
 */
export function pickLocation(locations, requestedId) {
  const withDesks = (locations || []).filter(l => l.desk_required !== 0 && (l.desks || []).length > 0);
  return withDesks.find(l => l.id === requestedId) || withDesks[0] || null;
}
